import React, { useEffect, useState } from 'react';
import API from './axios';
import API_ROUTES from './routes';
import ContactForm from './contactForm';
import './contactMain.css';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import BASE_URL from '../../config';

const ContactMain = () => {
  const [tab, setTab] = useState('contacts');
  const [contacts, setContacts] = useState([]);
  const [sharedContacts, setSharedContacts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [history, setHistory] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loadingDevices, setLoadingDevices] = useState(true);

  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingContactId, setEditingContactId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sharedLinkModalOpen, setSharedLinkModalOpen] = useState(false);
  const [generatedCallLink, setGeneratedCallLink] = useState('');
  const [statusMessage, setStatusMessage] = useState('');

  const modalFirstInputRef = React.useRef(null);
  const sharedLinkInputRef = React.useRef(null);

  const announceStatus = (msg) => {
    setStatusMessage('');
    setTimeout(() => setStatusMessage(msg), 50);
  };

  const handleModalKeyDown = (e) => {
    if (e.key === 'Escape') {
      setModalOpen(false);
    }
  };

  const handleSharedLinkModalKeyDown = (e) => {
    if (e.key === 'Escape') {
      setSharedLinkModalOpen(false);
    }
  };

  React.useEffect(() => {
    if (modalOpen && modalFirstInputRef.current) {
      modalFirstInputRef.current.focus();
    }
  }, [modalOpen]);

  React.useEffect(() => {
    if (sharedLinkModalOpen && sharedLinkInputRef.current) {
      sharedLinkInputRef.current.focus();
    }
  }, [sharedLinkModalOpen]);

  const [newContact, setNewContact] = useState({
    name: '',
    phone_number: '',
    email: '',
    serial: '',
    is_favorite: false,
    alias: '',
  });

  const [groupName, setGroupName] = useState('');
  const [serial, setSerial] = useState('');

  const token = localStorage.getItem('jwt_token');

  const fetchDevices = async () => {
    try {
      const response = await API.post(
        `${BASE_URL}/api/get_cust_devices/`,
        {},
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setDevices(response.data);

      // Auto-select first device if available
      if (response.data.length > 0) {
        const firstSerial = response.data[0].serial;
        setNewContact(prev => ({
          ...prev,
          serial: firstSerial
        }));
      }
    } catch (error) {
      console.error('Error fetching devices:', error);
    } finally {
      setLoadingDevices(false);
    }
  };

  const fetchContacts = async () => {
    const res = await API.get(API_ROUTES.CONTACTS);
    setContacts(res.data);
  };

  const fetchSharedContacts = async () => {
    try {
      const res = await API.get(API_ROUTES.SHARED_CONTACTS);
      setSharedContacts(res.data);
    } catch (error) {
      console.error('Error fetching shared contacts:', error);
    }
  };

  const fetchGroups = async () => {
    const res = await API.get(API_ROUTES.GROUPS);
    setGroups(res.data);
  };

  const fetchHistory = async () => {
    const res = await API.get(API_ROUTES.HISTORY);
    setHistory(res.data);
  };

  useEffect(() => {
    fetchDevices();
    fetchContacts();
    fetchSharedContacts();
    fetchGroups();
    fetchHistory();
  }, []);

  const handleAddOrUpdateContact = async () => {
    if (editMode) {
      await API.patch(API_ROUTES.UPDATE_CONTACT(editingContactId), newContact);
      toast.success('Contact updated!');
      announceStatus('Contact updated successfully.');
    } else {
      await API.post(API_ROUTES.CREATE_CONTACT, newContact);
      toast.success('Contact added!');
      announceStatus('Contact added successfully.');
    }
    setModalOpen(false);
    setEditMode(false);
    setEditingContactId(null);
    setNewContact({ name: '', phone_number: '', email: '', serial: '', is_favorite: false, alias: '' });
    fetchContacts();
  };

  const handleEditContact = (contact) => {
    setNewContact({
      name: contact.name,
      phone_number: contact.phone_number,
      email: contact.email || '',
      serial: contact.serial || '',
      is_favorite: contact.is_favorite,
      alias: contact.alias || '',
    });
    setEditingContactId(contact.id);
    setEditMode(true);
    setModalOpen(true);
  };

  const handleDeleteContact = async (id) => {
    await API.delete(API_ROUTES.DELETE_CONTACT(id));
    toast.success('Contact deleted!');
    announceStatus('Contact deleted.');
    fetchContacts();
  };

  const handleAddGroup = async () => {
    await API.post(API_ROUTES.CREATE_GROUP, { group_name: groupName, serial });
    toast.success('Group added!');
    setGroupName('');
    setSerial('');
    fetchGroups();
  };

  const handleCreateSharedContact = async () => {
    // Check if shared contact already exists for this device
    const existingSharedContact = sharedContacts.find(
      sc => sc.serial === newContact.serial
    );

    if (existingSharedContact) {
      toast.error('A shared contact already exists for this device!');
      return;
    }

    try {
      const response = await API.post(API_ROUTES.CREATE_SHARED_CONTACT, {
        serial: newContact.serial,
        name: 'Shared Contact',
        phone_number: '',
        email: '',
      });
      setGeneratedCallLink(response.data.calllink);
      setSharedLinkModalOpen(true);
      fetchSharedContacts(); // Refresh shared contacts list
      toast.success('Shared contact created successfully!');
    } catch (error) {
      console.error('Error creating shared contact:', error);
      toast.error('Failed to create shared contact. Please try again.');
    }
  };

  const handleResetSharedContact = async (sharedContactId) => {
    try {
      // Use the new reset-link API endpoint
      const response = await API.post(API_ROUTES.RESET_SHARED_CONTACT_LINK(sharedContactId));

      setGeneratedCallLink(response.data.data.calllink);
      setSharedLinkModalOpen(true);
      fetchSharedContacts(); // Refresh shared contacts list
      toast.success('Shared link reset successfully!');
    } catch (error) {
      console.error('Error resetting shared contact:', error);
      toast.error('Failed to reset shared link. Please try again.');
    }
  };

  const getSharedLinkForContact = (phone_number) => {
    return sharedContacts.find(sc => sc.phone_number === phone_number);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Link copied to clipboard!');
  };

  const filteredContacts = contacts.filter((contact) =>
    contact.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    contact.phone_number.includes(searchQuery)
  );

  return (
    <div className="dark-theme">
      <div className="dashboard-container">
        <h1>Contacts</h1>
        <div
          role="status"
          aria-live="polite"
          aria-atomic="true"
          style={{ position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap' }}
        >
          {statusMessage}
        </div>
        <div className="tab-buttons">
          {/* <button className={`dashboard-tab-button ${tab === 'contacts' ? 'dashboard-tab-active' : ''}`} onClick={() => setTab('contacts')}>Contacts</button> */}
          {/* <button className={`dashboard-tab-button ${tab === 'groups' ? 'dashboard-tab-active' : ''}`} onClick={() => setTab('groups')}>Groups</button> */}
          {/* <button className={`dashboard-tab-button ${tab === 'history' ? 'dashboard-tab-active' : ''}`} onClick={() => setTab('history')}>History</button> */}
        </div>

        {tab === 'contacts' && (
          <section className="dashboard-section">
            <div className="dashboard-section-header">
              <h2>My Contacts</h2>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  className="dashboard-add-button"
                  aria-haspopup="dialog"
                  onClick={() => {
                    setModalOpen(true);
                    setEditMode(false);
                    setNewContact(prev => ({
                      name: '',
                      phone_number: '',
                      email: '',
                      serial: prev.serial,
                      is_favorite: false,
                      alias: ''
                    }));
                  }}
                >
                  + Add Contact
                </button>
                {!sharedContacts.find(sc => sc.serial === newContact.serial) && (
                  <button
                    className="dashboard-add-button"
                    onClick={handleCreateSharedContact}
                    aria-label="Create shared contact for current device"
                  >
                    + Create Shared Contact
                  </button>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
              <input
                className="dashboard-input"
                type="text"
                placeholder="Search contacts..."
                aria-label="Search contacts by name or phone number"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ flex: 1 }}
              />
              <FormControl style={{ minWidth: '200px' }}>
                <InputLabel style={{ color: 'white' }} id="device-select-label">Select Device</InputLabel>
                <Select
                  value={newContact.serial}
                  onChange={(e) => setNewContact({ ...newContact, serial: e.target.value })}
                  label="Select Device"
                  labelId="device-select-label"
                  inputProps={{ 'aria-label': 'Select device' }}
                  sx={{
                    color: 'white',
                    '.MuiOutlinedInput-notchedOutline': {
                      borderColor: 'white',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'white',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'white',
                    },
                    '.MuiSvgIcon-root': {
                      color: 'white',
                    }
                  }}
                >
                  {devices.map((device) => (
                    <MenuItem key={device.uid} value={device.serial}>
                      {device.serial}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            <ul className="dashboard-list" aria-label="My contacts">
              {filteredContacts.length === 0 && searchQuery && (
                <li style={{ padding: '20px', textAlign: 'center', color: '#888' }} role="status">
                  No contacts match your search
                </li>
              )}
              {filteredContacts.map((c) => {
                const sharedLink = getSharedLinkForContact(c.phone_number);
                return (
                  <li className="dashboard-contact-item" key={c.id}>
                    <div style={{ flex: 1 }}>
                      <div>
                        {c.name} ({c.phone_number}){' '}
                        {c.is_favorite && <span aria-label="Favourite">★</span>}
                      </div>
                      {sharedLink && (
                        <div className="shared-link-actions">
                          <input
                            className="dashboard-input"
                            value={sharedLink.calllink}
                            readOnly
                            aria-label={`Shared call link for ${c.name}`}
                            aria-readonly="true"
                            style={{ flex: 1, fontSize: '12px', padding: '5px' }}
                          />
                          <div className="shared-link-buttons">
                            <button
                              className="dashboard-add-button"
                              onClick={() => copyToClipboard(sharedLink.calllink)}
                              aria-label={`Copy shared link for ${c.name}`}
                              style={{ padding: '5px 10px', fontSize: '12px' }}
                            >
                              Copy Link
                            </button>
                            <button
                              className="dashboard-edit-button"
                              onClick={() => handleResetSharedContact(sharedLink.id)}
                              aria-label={`Reset shared link for ${c.name}`}
                              style={{ padding: '5px 10px', fontSize: '12px' }}
                            >
                              Reset Link
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '5px' }}>
                      <button className="dashboard-edit-button" onClick={() => handleEditContact(c)} aria-label={`Edit ${c.name}`}>Edit</button>
                      <button className="dashboard-delete-btn" onClick={() => handleDeleteContact(c.id)} aria-label={`Delete ${c.name}`}>Delete</button>
                    </div>
                  </li>
                );
              })}
            </ul>

            <div className="dashboard-section-header" style={{ marginTop: '2rem' }}>
              <h2>Shared Contacts</h2>
            </div>
            <ul className="dashboard-list" aria-label="Shared contacts">
              {sharedContacts.length === 0 ? (
                <li style={{ padding: '20px', textAlign: 'center', color: '#888' }} role="status">
                  No shared contacts created yet
                </li>
              ) : (
                sharedContacts.map((sc) => (
                  <li className="dashboard-contact-item" key={sc.id}>
                    <div style={{ flex: 1 }}>
                      <div>
                        <strong>{sc.name}</strong> ({sc.phone_number})
                        {sc.email && <span style={{ color: '#888', marginLeft: '10px' }}>{sc.email}</span>}
                      </div>
                      <div style={{ fontSize: '12px', color: '#888', marginTop: '5px' }}>
                        Device: {sc.serial}
                      </div>
                      <div className="shared-link-actions">
                        <input
                          className="dashboard-input"
                          value={sc.calllink}
                          readOnly
                          aria-label={`Shared call link for ${sc.name} on device ${sc.serial}`}
                          aria-readonly="true"
                          style={{ flex: 1, fontSize: '12px', padding: '5px' }}
                        />
                        <div className="shared-link-buttons">
                          <button
                            className="dashboard-add-button"
                            onClick={() => copyToClipboard(sc.calllink)}
                            aria-label={`Copy shared link for ${sc.name}`}
                            style={{ padding: '5px 10px', fontSize: '12px' }}
                          >
                            Copy Link
                          </button>
                          <button
                            className="dashboard-edit-button"
                            onClick={() => handleResetSharedContact(sc.id)}
                            aria-label={`Reset shared link for ${sc.name}`}
                            style={{ padding: '5px 10px', fontSize: '12px' }}
                          >
                            Reset Link
                          </button>
                          <button
                            className="dashboard-delete-btn"
                            onClick={async () => {
                              try {
                                await API.delete(API_ROUTES.DELETE_SHARED_CONTACT(sc.id));
                                toast.success('Shared contact deleted!');
                                announceStatus(`Shared contact ${sc.name} deleted.`);
                                fetchSharedContacts();
                              } catch (error) {
                                console.error('Error deleting shared contact:', error);
                                toast.error('Failed to delete shared contact');
                              }
                            }}
                            aria-label={`Delete shared contact ${sc.name}`}
                            style={{ padding: '5px 10px', fontSize: '12px' }}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  </li>
                ))
              )}
            </ul>
          </section>
        )}

        {tab === 'groups' && (
          <section className="dashboard-section">
            <h2 className="dashboard-section-header">Groups</h2>
            <div className="dashboard-input-group">
              <input className="dashboard-input" placeholder="Group Name" value={groupName} onChange={(e) => setGroupName(e.target.value)} />
              <input className="dashboard-input" placeholder="Serial" value={serial} onChange={(e) => setSerial(e.target.value)} />
              <button className="dashboard-add-button" onClick={handleAddGroup}>Add Group</button>
            </div>
            <ul className="dashboard-list">
              {groups.map((g) => (
                <li className="dashboard-group-item" key={g.id}>{g.group_name}</li>
              ))}
            </ul>
          </section>
        )}

        {tab === 'history' && (
          <section className="dashboard-section">
            <h2 className="dashboard-section-header">Communication History</h2>
            <ul className="dashboard-list">
              {history.map((h) => (
                <li className="dashboard-history-item" key={h.id}>
                  {h.type} {h.direction} - Contact #{h.contact} at {new Date(h.timestamp).toLocaleString()}
                </li>
              ))}
            </ul>
          </section>
        )}

        {modalOpen && (
          <div
            className="dashboard-modal-backdrop"
            onKeyDown={handleModalKeyDown}
            onClick={(e) => { if (e.target === e.currentTarget) setModalOpen(false); }}
          >
            <div
              className="dashboard-modal-content"
              role="dialog"
              aria-modal="true"
              aria-labelledby="contact-modal-title"
            >
              <h3 id="contact-modal-title">{editMode ? 'Edit Contact' : 'Add Contact'}</h3>
              <form onSubmit={(e) => { e.preventDefault(); handleAddOrUpdateContact(); }}>
                <div className="dashboard-input-group">
                  <label htmlFor="contact-name" style={{ color: '#aab7c4', fontSize: '0.875rem' }}>
                    Name <span aria-hidden="true">*</span>
                  </label>
                  <input
                    id="contact-name"
                    className="dashboard-input"
                    required
                    name="name"
                    placeholder="Name"
                    value={newContact.name}
                    onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                    ref={modalFirstInputRef}
                  />
                  <label htmlFor="contact-phone" style={{ color: '#aab7c4', fontSize: '0.875rem' }}>
                    Phone Number <span aria-hidden="true">*</span>
                  </label>
                  <input
                    id="contact-phone"
                    className="dashboard-input"
                    required
                    name="phone_number"
                    placeholder="Phone Number"
                    value={newContact.phone_number}
                    onChange={(e) => setNewContact({ ...newContact, phone_number: e.target.value })}
                  />
                  <label htmlFor="contact-email" style={{ color: '#aab7c4', fontSize: '0.875rem' }}>Email</label>
                  <input
                    id="contact-email"
                    className="dashboard-input"
                    name="email"
                    placeholder="Email"
                    value={newContact.email}
                    onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                  />
                  <label htmlFor="contact-serial" style={{ color: '#aab7c4', fontSize: '0.875rem' }}>Serial</label>
                  <input
                    id="contact-serial"
                    className="dashboard-input"
                    name="serial"
                    placeholder="Serial"
                    value={newContact.serial}
                    readOnly
                    aria-readonly="true"
                    aria-describedby="serial-hint"
                    style={{ backgroundColor: '#2a2a2a', cursor: 'not-allowed' }}
                  />
                  <span id="serial-hint" style={{ fontSize: '0.75rem', color: '#888' }}>Auto-filled from selected device</span>
                  <label htmlFor="contact-alias" style={{ color: '#aab7c4', fontSize: '0.875rem' }}>Alias</label>
                  <input
                    id="contact-alias"
                    className="dashboard-input"
                    name="alias"
                    placeholder="Alias"
                    value={newContact.alias}
                    onChange={(e) => setNewContact({ ...newContact, alias: e.target.value })}
                  />
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#aab7c4', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={newContact.is_favorite}
                      onChange={(e) => setNewContact({ ...newContact, is_favorite: e.target.checked })}
                    />
                    Favourite
                  </label>
                </div>
                <button type="submit" className="dashboard-add-button">{editMode ? 'Update' : 'Submit'}</button>
                <button type="button" className="dashboard-modal-close" onClick={() => setModalOpen(false)}>Cancel</button>
              </form>
            </div>
          </div>
        )}

        {sharedLinkModalOpen && (
          <div
            className="dashboard-modal-backdrop"
            onKeyDown={handleSharedLinkModalKeyDown}
            onClick={(e) => { if (e.target === e.currentTarget) setSharedLinkModalOpen(false); }}
          >
            <div
              className="dashboard-modal-content"
              role="dialog"
              aria-modal="true"
              aria-labelledby="shared-link-modal-title"
            >
              <h3 id="shared-link-modal-title">Shared Contact Link Generated</h3>
              <p>Share this link with anyone to allow them to make calls:</p>
              <div style={{ display: 'flex', gap: '10px', marginTop: '15px', marginBottom: '15px' }}>
                <input
                  className="dashboard-input"
                  value={generatedCallLink}
                  readOnly
                  aria-label="Generated shared call link"
                  aria-readonly="true"
                  ref={sharedLinkInputRef}
                  style={{ flex: 1 }}
                />
                <button
                  className="dashboard-add-button"
                  onClick={() => copyToClipboard(generatedCallLink)}
                  aria-label="Copy generated shared call link"
                >
                  Copy
                </button>
              </div>
              <button
                type="button"
                className="dashboard-modal-close"
                onClick={() => setSharedLinkModalOpen(false)}
              >
                Close
              </button>
            </div>
          </div>
        )}

        <ToastContainer position="top-right" autoClose={3000} hideProgressBar />
      </div>
    </div>
  );
};

export default ContactMain;
